import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import DefaultLocales from 'globals/locales/default';
import { observer } from 'mobx-react';
import FsButton from 'ui-elements/buttons/FsButton';
import { Alert, AlertTitle } from '@material-ui/lab';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import reportLocales from 'globals/locales/report';
import { Box, TextField } from '@material-ui/core';
import Routes from 'routes';
import FsDialogTitle from 'ui-elements/modals/FsDialogTitle';
import { wait } from 'globals/utils';
import { useReportContext } from 'contexts/report/ReportContext';
import { ModalState } from 'globals/types';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import {
  CustomDialog,
  DialogActions,
  DialogContent,
} from '../ReportModals.style-elements';

const { delete: deleteLabel, cancel: cancelLabel } = DefaultLocales;
const actionWord = 'DELETE';
const { dialogTitle, alertTitle, alertDescription } = reportLocales.deleteModal;

export interface ReportDeleteModalLocationStateProps {
  success?: ModalState;
  path?: string;
}

const ReportDeleteModal: React.FC<RouteComponentProps> = ({ history }) => {
  const {
    report: { userId },
  } = useReportContext();
  const [pending, setPending] = useState<boolean>(false);
  const [confirmationPhrase, setConfirmationPhrase] = React.useState<string>(
    ''
  );
  const [deletionStatus, setDeletionStatus] = React.useState<boolean>(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmationPhrase(event.target.value);
  };

  const handleClose = (): void => {
    history.push({ pathname: Routes.REPORT });
  };

  const handleSend = (): boolean => {
    // TODO: API | bind with proper endpoint
    const payload = {
      userId,
    };
    // eslint-disable-next-line no-console
    console.log('payload', JSON.stringify(payload, null, 2));
    return Math.random() < 0.5;
  };

  const redirectToModalFail = (): void => {
    const pathname = Routes.REPORT_MODAL_DELETE_FAIL;
    const state: ReportDeleteModalLocationStateProps = {
      success: ModalState.FAIL,
    };
    history.push({
      pathname,
      state,
    });
  };

  const redirectToModalSuccess = (): void => {
    const pathname = Routes.REPORT_MODAL_DELETE_SUCCESS;
    const state: ReportDeleteModalLocationStateProps = {
      success: ModalState.SUCCESS,
    };
    history.push({
      pathname,
      state,
    });
  };

  const handleDeleteAndRedirect = async (): Promise<void> => {
    const sendStatus = handleSend();
    setPending(true);
    await wait(300);
    setPending(false);
    handleClose();
    if (sendStatus) {
      redirectToModalSuccess();
      return;
    }
    redirectToModalFail();
  };

  useEffect(() => {
    setDeletionStatus(confirmationPhrase === actionWord);
  }, [confirmationPhrase]);

  return (
    <CustomDialog
      disableEnforceFocus
      open
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      data-test="report-delete-modal"
    >
      <FsDialogTitle id="report-delete-modal" onClose={handleClose}>
        {dialogTitle}
      </FsDialogTitle>

      <DialogContent dividers>
        <Alert severity="error">
          <AlertTitle>
            <FsBodyBold>{alertTitle}</FsBodyBold>
          </AlertTitle>
          <FsBody>{alertDescription[0]}</FsBody>
          <Box marginTop={2}>
            <TextField
              value={confirmationPhrase}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              placeholder="Type DELETE here and click Delete button"
              data-test="report-delete-modal-input"
            />
          </Box>
        </Alert>
      </DialogContent>
      <DialogActions>
        <FsButton onClick={handleClose} color="primary">
          {cancelLabel}
        </FsButton>
        <FsPendingButton
          pending={pending}
          disabled={!deletionStatus}
          onClick={handleDeleteAndRedirect}
          data-test="report-delete-modal-button"
          startIcon={<DeleteOutlineOutlinedIcon />}
        >
          {deleteLabel}
        </FsPendingButton>
      </DialogActions>
    </CustomDialog>
  );
};

export default observer(ReportDeleteModal);
